import React, { Component } from 'react';
import { AlertTriangle, RefreshCw, Home, Bug } from 'lucide-react';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null, 
      errorInfo: null,
      retryCount: 0,
      isRecovering: false
    };
    this.maxRetries = 3;
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });
    
    // Log error to monitoring service
    this.logError(error, errorInfo);
    
    // Attempt auto-recovery
    this.attemptAutoRecovery();
  }

  logError = (error, errorInfo) => {
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo?.componentStack,
      timestamp: new Date().toISOString(),
      userAgent: navigator.userAgent,
      url: window.location.href,
      localStorage: this.getSafeLocalStorage()
    };

    // Log to console in development
    console.error('Error Boundary caught error:', errorData);

    // Send to backend for logging
    try {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/errors/log`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(errorData)
      }).catch(() => {});
    } catch (e) {}
  };

  getSafeLocalStorage = () => {
    try {
      return {
        token: localStorage.getItem('token') ? 'present' : 'absent',
        theme: localStorage.getItem('theme'),
        language: localStorage.getItem('language')
      };
    } catch {
      return {};
    }
  };

  attemptAutoRecovery = () => {
    const { retryCount } = this.state;
    
    if (retryCount < this.maxRetries) {
      this.setState({ isRecovering: true });
      
      setTimeout(() => {
        // Clear potentially corrupted state
        this.clearCorruptedData();
        
        this.setState(prevState => ({
          hasError: false,
          error: null,
          errorInfo: null,
          retryCount: prevState.retryCount + 1,
          isRecovering: false
        }));
      }, 1000 * (retryCount + 1)); // Exponential backoff
    }
  };

  clearCorruptedData = () => {
    try {
      // Clear session storage but preserve auth
      const token = localStorage.getItem('token');
      const user = localStorage.getItem('user');
      sessionStorage.clear();
      if (token) localStorage.setItem('token', token);
      if (user) localStorage.setItem('user', user);
    } catch (e) {}
  };

  handleRefresh = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleClearAndReload = () => {
    try {
      localStorage.clear();
      sessionStorage.clear();
    } catch (e) {}
    window.location.href = '/';
  };

  render() {
    const { hasError, error, isRecovering, retryCount } = this.state;
    const { children, fallback } = this.props;

    if (isRecovering) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center p-8">
            <div className="animate-spin rounded-full h-12 w-12 border-4 border-blue-500 border-t-transparent mx-auto mb-4" />
            <p className="text-gray-600">Auto-recovering... Attempt {retryCount + 1}/{this.maxRetries}</p>
          </div>
        </div>
      );
    }

    if (hasError) {
      if (fallback) return fallback;

      return (
        <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 p-4">
          <div className="max-w-md w-full bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <AlertTriangle className="w-8 h-8 text-red-500" />
            </div>
            
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Something went wrong</h1>
            <p className="text-gray-600 mb-6">
              We encountered an unexpected error. Our auto-recovery system attempted to fix it {retryCount} time(s).
            </p>

            {error && (
              <div className="bg-gray-50 rounded-xl p-4 mb-6 text-left">
                <p className="text-xs font-mono text-gray-500 break-all">
                  {error.message || 'Unknown error'}
                </p>
              </div>
            )}

            <div className="space-y-3">
              <button
                onClick={this.handleRefresh}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-blue-500 text-white rounded-xl font-medium hover:bg-blue-600 transition-colors"
              >
                <RefreshCw className="w-4 h-4" />
                Try Again
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-colors"
              >
                <Home className="w-4 h-4" />
                Go to Home
              </button>

              <button
                onClick={this.handleClearAndReload}
                className="w-full flex items-center justify-center gap-2 px-4 py-3 border border-gray-200 text-gray-600 rounded-xl font-medium hover:bg-gray-50 transition-colors text-sm"
              >
                <Bug className="w-4 h-4" />
                Clear Data & Restart
              </button>
            </div>

            <p className="text-xs text-gray-400 mt-6">
              Error ID: {Date.now().toString(36)}
            </p>
          </div>
        </div>
      );
    }

    return children;
  }
}

export default ErrorBoundary;
